import { useGetSafeClientCode, useIsRootPage } from '@/hooks/router'
import { Link } from '@tanstack/react-router'

import { useGetAllClients } from '@/gen/publicApi'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/avatar'
import { DropdownMenuGroup, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/dropdown-menu'
import { navigationMenuTriggerStyle } from '@/components/navigationMenu'
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '@/components/popover'

import { useIdentity } from '@/state/identity'

import { cn } from '@/utils/cn'

import LogoutMenuItem from '../-components/logoutMenuItem'

export function UserSettingsNavigationItem() {
    const client_code = useGetSafeClientCode()
    // Haven't put in pagination here yet. If someone has more than 500 clients
    // then we can figure this out then
    const { data: clients } = useGetAllClients({
        Limit: 500,
    })
    const { identity } = useIdentity()
    const isRoot = useIsRootPage()

    if (!identity) {
        return null
    }

    // Bit of a hack, but because the profile page is contained within the client
    // code sub route we can't redirect a user there without a selected client. So
    // Don't show this on the client select page
    const shouldShowProfileLink = !isRoot
    const shouldShowClientReselect = clients?.data && clients.data?.length > 1 && !isRoot
    // const isActive = match({ to: '/$client_code/settings', fuzzy: true })

    return (
        <Popover>
            <PopoverTrigger>
                <div className="items-end max-w-56">
                    <div className="flex-col m-0 space-x-0">
                        <div className="ml-0">
                            <div
                                className={cn(
                                    navigationMenuTriggerStyle(),
                                    'w-56 cursor-pointer justify-start rounded-none bg-transparent pl-6',
                                )}
                            >
                                <div className="flex overflow-hidden">
                                    <Avatar className="w-6 h-6 mr-2 uppercase">
                                        <AvatarImage src="/avatars/01.png" alt="@shadcn" />
                                        <AvatarFallback>{identity.identifier[0]}</AvatarFallback>
                                    </Avatar>
                                    {identity.name}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </PopoverTrigger>
            <PopoverContent side="right" className="w-56 px-0 pb-2 mb-2">
                <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col px-4 mb-2 space-y-1">
                        <p className="text-sm font-medium leading-none">{identity.name}</p>
                        <p className="text-xs leading-none text-muted-foreground">{identity.identifier}</p>
                    </div>
                </DropdownMenuLabel>
                {(shouldShowProfileLink || shouldShowClientReselect) && <DropdownMenuSeparator />}
                <DropdownMenuGroup>
                    {shouldShowProfileLink && (
                        <>
                            <DropdownMenuLabel asChild>
                                <Link
                                    className="block px-6 font-thin hover:bg-surface0"
                                    to="/$client_code/settings/invitations"
                                    params={{ client_code }}
                                >
                                    <PopoverClose>Invite other people</PopoverClose>
                                </Link>
                            </DropdownMenuLabel>
                            <DropdownMenuLabel asChild>
                                <Link
                                    className="block px-6 font-thin hover:bg-surface0"
                                    to="/$client_code/settings"
                                    params={{ client_code }}
                                >
                                    <PopoverClose>Settings</PopoverClose>
                                </Link>
                            </DropdownMenuLabel>
                        </>
                    )}
                    {shouldShowClientReselect && (
                        <DropdownMenuLabel asChild>
                            <Link className="block px-6 font-thin hover:bg-surface0" to="/" search={{ reselect: true }}>
                                <PopoverClose>Switch client</PopoverClose>
                            </Link>
                        </DropdownMenuLabel>
                    )}
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <LogoutMenuItem />
            </PopoverContent>
        </Popover>
    )
}
